import React, { useState, useEffect } from "react"
import {
  getDiscountByEnrollment,
  updateDiscount,
  DiscountResponse,
  UpdateDiscountRequest,
} from "../../../../service/discount"
import { toast } from "react-toastify"
import CommonSelect, { Option } from "../../../../core/common/commonSelect"

interface DiscountModalProps {
  studentData: any
  fetchData: () => void
  selectedId?: string | null
  setSelectedId?: (id: string | null) => void
}

const DISCOUNT_TYPES = [
  { label: "Percentage", value: "percentage" },
  { label: "Amount", value: "amount" },
]

const DiscountModal: React.FC<DiscountModalProps> = ({
  studentData,
  fetchData,
  setSelectedId,
}) => {
  const [discountValue, setDiscountValue] = useState("")
  const [discountType, setDiscountType] = useState<Option>(DISCOUNT_TYPES[0])
  const [loading, setLoading] = useState(false)
  const [fetchingDiscount, setFetchingDiscount] = useState(false)
  const [currentDiscount, setCurrentDiscount] =
    useState<DiscountResponse | null>(null)

  useEffect(() => {
    if (studentData) {
      fetchCurrentDiscount()
    }
  }, [studentData])

  useEffect(() => {
    const handleModalHide = () => {
      if (currentDiscount?.data?.discount) {
        setDiscountValue(
          currentDiscount?.data?.discount?.discount?.toString() || ""
        )

        if (currentDiscount.data.discount.discountType) {
          const type = DISCOUNT_TYPES.find(
            t => t.value === currentDiscount?.data?.discount?.discountType
          )
          if (type) {
            setDiscountType(type)
          }
        }
      } else {
        setDiscountValue("")
        setDiscountType(DISCOUNT_TYPES[0])
      }
    }

    const modal = document.getElementById("discount-modal")
    if (modal) {
      modal.addEventListener("hidden.bs.modal", handleModalHide)
    }

    return () => {
      if (modal) {
        modal.removeEventListener("hidden.bs.modal", handleModalHide)
      }
    }
  }, [currentDiscount])

  const fetchCurrentDiscount = async () => {
    if (!studentData?._id) return

    setFetchingDiscount(true)
    try {
      const response = await getDiscountByEnrollment(studentData._id)
      setCurrentDiscount(response)

      if (response.data?.discount) {
        setDiscountValue(response.data.discount.discount.toString())

        if (response.data.discount.discountType) {
          const type = DISCOUNT_TYPES.find(
            t => t.value === response?.data?.discount?.discountType
          )
          if (type) {
            setDiscountType(type)
          }
        }
      } else {
        setDiscountValue("")
        setDiscountType(DISCOUNT_TYPES[0])
      }
    } catch (error) {
      console.error("Error fetching discount:", error)
      setDiscountValue("")
      setDiscountType(DISCOUNT_TYPES[0])
    } finally {
      setFetchingDiscount(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!studentData) return

    setLoading(true)
    try {
      const discountData: UpdateDiscountRequest = {
        discount: discountValue || "0",
        discountType: discountType.value,
      }

      await updateDiscount(studentData._id, discountData)
      toast.success("Discount updated successfully!")
      fetchData()

      const el = document.getElementById("discount-modal")
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal")
        el.click()
        el.removeAttribute("data-bs-dismiss")
      }

      setSelectedId?.(null)
    } catch (error) {
      console.error("Error updating discount:", error)
      toast.error("Error updating discount. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (currentDiscount?.data?.discount) {
      setDiscountValue(
        currentDiscount?.data?.discount?.discount?.toString() || ""
      )
      if (currentDiscount.data.discount.discountType) {
        const type = DISCOUNT_TYPES.find(
          t => t.value === currentDiscount?.data?.discount?.discountType
        )
        if (type) {
          setDiscountType(type)
        }
      }
    } else {
      setDiscountValue("")
      setDiscountType(DISCOUNT_TYPES[0])
    }

    setSelectedId?.(null)
  }

  const handleBackdropClick = () => {
    handleClose()
  }

  const getPlaceholder = () => {
    if (discountType.value === "percentage") {
      return "Enter discount percentage"
    } else {
      return "Enter discount amount"
    }
  }

  const getMaxValue = () => {
    if (discountType.value === "percentage") {
      return "100"
    }
    return undefined
  }

  const getStepValue = () => {
    if (discountType.value === "percentage") {
      return "0.01"
    } else {
      return "1"
    }
  }

  const getDiscountLabel = () => {
    if (discountType.value === "percentage") {
      return "Discount"
    } else {
      return "Discount Amount"
    }
  }

  return (
    <div
      className="modal fade"
      id="discount-modal"
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      onClick={e => {
        if (e.target === e.currentTarget) {
          handleBackdropClick()
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              {currentDiscount?.data?.discount ? "Update" : "Add"} Discount -{" "}
              {studentData?.user?.firstName} {studentData?.user?.lastName}
            </h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleClose}
            >
              <i className="ti ti-x" />{" "}
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {fetchingDiscount ? (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading discount...</span>
                  </div>
                  <p className="mt-2">Loading discount information...</p>
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">GR Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={studentData?.student?.grNumber || "N/A"}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Class</label>
                      <input
                        type="text"
                        className="form-control"
                        value={studentData?.class?.name || "N/A"}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Discount Type</label>
                      <CommonSelect
                        className="select"
                        options={DISCOUNT_TYPES}
                        value={discountType}
                        onChange={selectedOption =>
                          setDiscountType(selectedOption as Option)
                        }
                        isDisabled={loading || fetchingDiscount}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">{getDiscountLabel()}</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder={getPlaceholder()}
                        value={discountValue}
                        onChange={e => setDiscountValue(e.target.value)}
                        min="0"
                        max={getMaxValue()}
                        step={getStepValue()}
                        required
                        disabled={loading || fetchingDiscount}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={handleClose}
                disabled={loading || fetchingDiscount}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || fetchingDiscount}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />{" "}
                    {currentDiscount?.data?.discount
                      ? "Updating..."
                      : "Applying..."}
                  </>
                ) : currentDiscount?.data?.discount ? (
                  "Update Discount"
                ) : (
                  "Apply Discount"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default DiscountModal
